import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { AudioPlayButton } from '@/components/ui/AudioPlayButton';
import { wotdApi } from '@/api/wotd';

/**
 * Слово дня для человека, который ещё не зарегистрировался.
 *
 * ЗАЧЕМ. До 20.08.2026 на платформе нельзя было попробовать ничего, не создав
 * аккаунт: первый экран предлагал регистрацию «на веру». Слово дня — самая
 * дешёвая честная проба: оно одно на всех, генерируется раз в сутки и уже лежит
 * готовым, поэтому гостю не нужен ни аккаунт, ни расход на генерацию.
 *
 * Читаем публичную ручку без входа. Отметки «изучено» здесь нет намеренно: она
 * хранится за пользователем, а гостя нет — сохранить прогресс можно только
 * зарегистрировавшись, и это как раз повод.
 */
export function GuestWordOfTheDay() {
  const { t, i18n } = useTranslation();

  const wotd = useQuery({
    queryKey: ['public', 'wotd'],
    queryFn: wotdApi.getTodayPublic,
    // Слово меняется раз в сутки — держим в кеше, чтобы каждое открытие
    // лендинга не дёргало сервер.
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  const w = wotd.data?.word;
  // Молча ничего не показываем: пустая карточка с надписью «не загрузилось» на
  // первом экране хуже, чем отсутствие блока.
  if (!w) return null;

  const lang = i18n.language.split('-')[0];
  const translation = lang === 'kk' ? w.translation_kk : lang === 'en' ? w.definition : w.translation_ru;

  return (
    <div className="rounded-[22px] bg-white p-5 shadow-[0_18px_50px_-28px_rgba(0,0,0,.55)]">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#6D5AE0]">{t('wotd.kicker')}</p>
      <div className="mt-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-[28px] font-extrabold leading-tight text-[#3A2E8C]">{w.word}</p>
          {w.ipa && <p className="mt-0.5 text-sm text-[#6D5AE0]">{w.ipa}</p>}
          {translation && <p className="mt-2 text-[15px] text-on-surface">{translation}</p>}
        </div>
        {/*
          ⚠️ ОБЩАЯ КНОПКА, А НЕ СВОЙ `new Audio()` (12.09.2026). Здесь до
          последнего жила пятая копия озвучки: отказ гасился пустым `catch`, и
          гость на плохой связи видел кнопку, которая просто ничего не делает —
          ни загрузки, ни ошибки, ни повтора. Общая кнопка показывает всё три.
        */}
        {w.audio_url && (
          <AudioPlayButton
            src={w.audio_url}
            className="h-11 w-11 shrink-0 rounded-full bg-[#6D5AE0] text-white hover:brightness-110 hover:bg-[#6D5AE0]"
          />
        )}
      </div>
      {w.example && <p className="mt-3 text-[14px] italic leading-relaxed text-on-surface-variant">{w.example}</p>}
    </div>
  );
}
